import React from 'react';

import { Button as HeadlessButton } from '@headlessui/react';

import cn from '~/utils/cn';

export type TextButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning';

export interface TextButtonProps {
  children: React.ReactNode;
  variant?: TextButtonVariant;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  id?: string;
  name?: string;
  value?: string;
  form?: string;
  ariaLabel?: string;
  ariaDescribedby?: string;
}

const TextButton: React.FC<TextButtonProps> = ({
  children,
  variant = 'primary',
  disabled = false,
  onClick,
  className = '',
  type = 'button',
  id,
  name,
  value,
  form,
  ariaLabel,
  ariaDescribedby,
}) => {
  // Variant styles for text buttons
  const variantStyles: Record<TextButtonVariant, string> = {
    primary: 'text-blue-1000 hover:text-blue-700',
    secondary: 'text-grey-700 hover:text-grey-800',
    success: 'text-green-700 hover:text-green-600',
    danger: 'text-red-700 hover:opacity-80',
    warning: 'text-yellow-700 hover:text-yellow-800',
  };

  const baseStyles =
    'font-medium transition-colors cursor-pointer focus:outline-none hover:bg-slate-300 rounded-md px-12 py-2';

  const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <HeadlessButton
      as="button"
      id={id}
      name={name}
      value={value}
      form={form}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(baseStyles, variantStyles[variant], disabledStyles, className)}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
    >
      {children}
    </HeadlessButton>
  );
};

export default TextButton;
